import React, { useState, ChangeEvent, MouseEvent, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { GiftCategory, PendulumLogo } from "../../../../api/giftCategories";
import useUpdateCategoryMutation from "../hooks/useUpdateCategoryMutation";
import uploadFile from "../../../../utils/uploadFiles";
import ButtonLoader from "../../../../components/Widgets/ButtonLoader";
import KlydosPickDialog from "./KlydosPickDialog";
import LogosPickDialog from "./LogosPickDialog";
import DSButton from "../../../system-design/DSButton";
import useGreetCategoryKlydoStatsQuery from "../hooks/useGreetCategoryKlydoStatsQuery";
import useKlydo from "../../../../components/hooks/useKlydo";
import { isVideo, resizeCloudinary } from "../../../../utils/util";
import KlydoModal from "../../../Klydos/components/KlydoModal";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSBox from "../../../system-design/DSBox";
import DSGrid from "../../../system-design/DSGrid";
import DSTextField from "../../../system-design/DSTextField";
import DSTypography from "../../../system-design/DSTypography";
import DSList from "../../../system-design/DSList";
import DSListItem from "../../../system-design/DSListItem";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSCard from "../../../system-design/DSCard";
import DSUploadButton from "../../../system-design/DSUploadButton";
import { IconButton } from "@mui/material";
import DSTooltip from "../../../system-design/DSTooltip";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";

interface KlydoListItemWithGreetsProps {
  klydoId: string;
  greetCount: number;
  onRemove: (klydoId: string) => void;
}

const KlydoListItemWithGreets: React.FC<KlydoListItemWithGreetsProps> = ({
  klydoId,
  greetCount,
  onRemove,
}) => {
  const [showKlydoModal, setShowKlydoModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { data: klydo, isLoading } = useKlydo(klydoId);

  const handleKlydoSelect = (event: MouseEvent) => {
    event.preventDefault();
    setShowKlydoModal(true);
  };

  const handleRemove = () => {
    setDeleting(true);
    if (onRemove && klydo?.id) onRemove(klydo.id);
    setDeleting(false);
  };

  return (
    <>
      {klydo && !isLoading && (
        <DSListItem
          key={klydo?.id}
          onClick={handleKlydoSelect}
          sx={{
            border: 0,
            paddingTop: 0,
            paddingBottom: 0,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <DSBox
            sx={{
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <DSBox sx={{ flex: "0 0 30%" }} title={klydo?.id}>
              {klydo?.name}
            </DSBox>
            <DSBox sx={{ flex: "0 0 30%" }} title={klydo?.creator}>
              {klydo?.author?.name || klydo?.creator}
            </DSBox>
            {!klydo?.loopUrl || isVideo(klydo?.loopUrl) === "none" ? (
              <DSBox
                sx={{
                  flex: "0 0 15%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <DSTypography>--</DSTypography>
              </DSBox>
            ) : (
              <DSBox
                sx={{
                  flex: "0 0 15%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <DSBox
                  sx={{
                    borderRadius: "50%",
                    width: 35,
                    height: 35,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <DSBox
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      position: "absolute",
                      top: "0%",
                      left: "0%",
                      width: "100%",
                      aspectRatio: "1/1",
                    }}
                  >
                    {isVideo(klydo?.loopUrl) === "image" ? (
                      <img
                        loading="lazy"
                        alt="gif"
                        src={resizeCloudinary(klydo?.loopUrl, 48)}
                        style={{
                          width: "auto",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <video
                        muted={true}
                        src={resizeCloudinary(klydo?.loopUrl, 48)}
                        loop
                        autoPlay
                        style={{
                          width: "auto",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </DSBox>
                </DSBox>
              </DSBox>
            )}
            <DSBox
              sx={{ flex: "0 0 10%", textAlign: "center", alignSelf: "center" }}
            >
              --
            </DSBox>
            <DSBox
              sx={{ flex: "0 0 10%", textAlign: "center", alignSelf: "center" }}
            >
              {greetCount}
            </DSBox>
            <DSBox
              sx={{ flex: "0 0 5%", display: "flex", justifyContent: "center" }}
            >
              {deleting ? (
                <DSCircularProgress size={16} />
              ) : (
                <IconButton
                  title="Remove klydo"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  sx={{ width: 24, height: 24 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </DSBox>
          </DSBox>
        </DSListItem>
      )}

      {showKlydoModal && klydo && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={showKlydoModal}
          onClose={() => setShowKlydoModal(false)}
        >
          <KlydoModal klydoId={klydo.id} setShowModal={setShowKlydoModal} />
        </DSDialog>
      )}
    </>
  );
};

const GiftCategoryModal = (props: {
  row?: GiftCategory;
  setShowModal: (b: boolean) => void;
  logos?: PendulumLogo[];
  logosLoading?: boolean;
}) => {
  const [giftCategoryImageUrl, setGiftCategoryImageUrl] = useState<string>(
    props?.row?.categoryImageUrl || "",
  );

  const [pendulumLogoUrl, setPendulumLogoUrl] = useState<string>(
    props?.row?.pendulumLogoUrl || "",
  );
  const [name, setName] = useState<string>(props.row?.name || "");
  const [draftKlydoIds, setDraftKlydoIds] = useState<string[]>(
    props?.row?.klydoIds || [],
  );

  const { data: klydoStats } = useGreetCategoryKlydoStatsQuery(
    props.row?.name || "",
  );
  const [removedKlydoIds, setRemovedKlydoIds] = useState<string[]>([]);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [isKlydoModalOpen, setIsKlydoModalOpen] = useState(false);
  const [isLogosModalOpen, setIsLogosModalOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string>("");

  const { mutate: updateCategoryMutation } = useUpdateCategoryMutation({
    onSuccess: () => {
      setResponseMessage("Gift category updated successfully!");
      props.setShowModal(false);
    },
    onError: (error) => {
      setResponseMessage(
        `Failed to update gift category: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    },
  });

  const handleRemoveKlydo = (klydoId: string) => {
    removedKlydoIds.push(klydoId);
    setRemovedKlydoIds((prev) => prev.slice());
    if (draftKlydoIds) {
      draftKlydoIds.splice(draftKlydoIds.indexOf(klydoId), 1);
    }
    setDraftKlydoIds((prev) => prev?.slice());
  };

  const handleKlydoSelection = (selected: string[]) => {
    setDraftKlydoIds(selected);
  };

  const saveGiftCategory = async () => {
    updateCategoryMutation({
      _id: props.row!._id!,
      name: name,
      categoryImageUrl: giftCategoryImageUrl,
      klydoIds: draftKlydoIds,
      pendulumLogoUrl: pendulumLogoUrl,
    });
  };

  useEffect(() => {
    if (responseMessage) {
      alert(responseMessage);
    }
  }, [responseMessage]);

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 0,
          marginLeft: "10px",
          marginRight: "5px",
          marginBottom: "24px",
        }}
      >
        <DSTypography
          variant="h4"
          sx={{ margin: 0, paddingTop: "16px", paddingLeft: "14px" }}
        >
          Gift Category
        </DSTypography>
        <IconButton
          onClick={() => {
            props.setShowModal(false);
          }}
          sx={{ bottom: "7px", position: "relative" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DSBox>
      <DSBox>
        <DSBox sx={{ padding: 2 }}>
          <DSGrid container spacing={2}>
            <DSGrid item xs={12}>
              <form onSubmit={saveGiftCategory} id="newPackForm">
                <DSGrid container spacing={2}>
                  <DSGrid
                    item
                    sx={{
                      width: "80%",
                      mx: "auto",
                      backgroundColor: "paper.elevation4",
                      p: 2,
                      borderRadius: "16px",
                      mb: 2,
                    }}
                  >
                    <DSBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                        Gift Category's Name:
                      </DSTypography>
                      <DSBox sx={{ flex: 1 }}>
                        <DSTextField
                          value={name}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setName(e.target.value)
                          }
                          placeholder="Enter name..."
                          required
                          fullWidth
                          size="small"
                        />
                      </DSBox>
                    </DSBox>
                  </DSGrid>
                  <DSGrid
                    item
                    sx={{
                      display: "flex",
                      width: "80%",
                      mx: "auto",
                      gap: 2,
                      backgroundColor: "paper.elevation4",
                      p: 2,
                      borderRadius: "16px",
                    }}
                  >
                    <DSCard
                      sx={{ mb: 2, p: 2, textAlign: "center", width: "50%" }}
                    >
                      <DSBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <label style={{ width: "100%" }}>
                          <DSUploadButton
                            text={
                              giftCategoryImageUrl
                                ? "Change Logo Image"
                                : "Upload Logo Image"
                            }
                          />
                          <input
                            type="file"
                            hidden
                            accept="image/png"
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const fileReader = new FileReader();
                                fileReader.onload = () => {
                                  uploadFile({
                                    file: fileReader.result as string,
                                    folder: "giftCategories",
                                    upload_preset: "packpendulumconvers",
                                  }).then((url) =>
                                    setGiftCategoryImageUrl(url as string),
                                  );
                                };
                                fileReader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                        <DSTooltip
                          title="Only PNG images can be accepted"
                          placement="top"
                          arrow
                        >
                          <IconButton size="small" sx={{ ml: 1 }}>
                            <span
                              style={{
                                fontSize: "14px",
                                opacity: 0.7,
                                fontWeight: "bold",
                                border: "1px solid #ccc",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              ?
                            </span>
                          </IconButton>
                        </DSTooltip>
                      </DSBox>
                      {giftCategoryImageUrl && (
                        <DSBox>
                          <DSTypography
                            color="text.secondary"
                            variant="caption"
                          >
                            Current Logo Image Preview:
                          </DSTypography>
                          <br />
                          <img
                            src={giftCategoryImageUrl}
                            alt="Gift category preview"
                            style={{
                              maxWidth: "80px",
                              maxHeight: "80px",
                              borderRadius: "8px",
                              marginTop: "5px",
                            }}
                          />
                        </DSBox>
                      )}
                    </DSCard>

                    <DSCard
                      sx={{
                        mb: 2,
                        p: 2,
                        width: "50%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        textAlign: "center",
                      }}
                    >
                      <DSPrimaryButton
                        sx={{ mb: 2, color: "gray" }}
                        onClick={() => setIsLogosModalOpen(!isLogosModalOpen)}
                      >
                        Select pendulum logo from existing options
                      </DSPrimaryButton>
                      {pendulumLogoUrl && (
                        <DSBox>
                          <DSTypography
                            color="text.secondary"
                            variant="caption"
                          >
                            Current Logo Pendulum Preview:
                          </DSTypography>
                          <br />
                          <img
                            src={pendulumLogoUrl}
                            alt="Pendulum logo preview"
                            style={{
                              maxWidth: "100px",
                              maxHeight: "100px",
                              borderRadius: "4px",
                              marginTop: "5px",
                            }}
                          />
                        </DSBox>
                      )}
                    </DSCard>
                  </DSGrid>

                  <DSGrid
                    item
                    sx={{
                      width: "80%",
                      mx: "auto",
                      backgroundColor: "paper.elevation4",
                      p: 2,
                      borderRadius: "16px",
                      mt: 2,
                    }}
                  >
                    <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                      Klydos:
                    </DSTypography>
                    {draftKlydoIds && draftKlydoIds.length > 0 ? (
                      <DSCard sx={{ mb: 2, p: 2, width: "100%" }}>
                        <DSList
                          sx={{
                            width: "100%",
                            fontWeight: "bold",
                          }}
                        >
                          <DSListItem>
                            <DSBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "12px",
                                color: "gray",
                                border: 0,
                                width: "100%",
                              }}
                            >
                              <DSBox sx={{ flex: "0 0 30%" }}>IDF</DSBox>
                              <DSBox sx={{ flex: "0 0 30%" }}>Author</DSBox>
                              <DSBox
                                sx={{ flex: "0 0 15%", textAlign: "center" }}
                              >
                                GIF
                              </DSBox>
                              <DSBox
                                sx={{ flex: "0 0 10%", textAlign: "center" }}
                              >
                                Pool
                              </DSBox>
                              <DSBox
                                sx={{ flex: "0 0 10%", textAlign: "center" }}
                              >
                                Greets
                              </DSBox>
                              <DSBox
                                sx={{ flex: "0 0 5%", textAlign: "center" }}
                              ></DSBox>
                            </DSBox>
                          </DSListItem>
                        </DSList>
                        <DSList
                          sx={{
                            width: "100%",
                            borderRadius: 0,
                            border: "none",
                            maxHeight: "190px",
                            overflowY: "auto",
                          }}
                        >
                          {draftKlydoIds?.map((klydoId) => {
                            const greetCount =
                              klydoStats?.find(
                                (stat) => stat.klydoId === klydoId,
                              )?.greetCount || 0;

                            return (
                              <KlydoListItemWithGreets
                                key={klydoId}
                                klydoId={klydoId}
                                greetCount={greetCount}
                                onRemove={handleRemoveKlydo}
                              />
                            );
                          })}
                        </DSList>
                      </DSCard>
                    ) : draftKlydoIds ? (
                      <DSTypography>No klydos in draft</DSTypography>
                    ) : (
                      <DSCircularProgress />
                    )}
                    <DSBox
                      sx={{
                        width: "90%",
                        flexDirection: "column",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <DSBox
                        sx={{
                          width: "90%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          margin: "0 10px 10px 10px",
                        }}
                      >
                        <DSButton
                          type="button"
                          onClick={() => {
                            setIsKlydoModalOpen(true);
                          }}
                        >
                          Select Klydos
                        </DSButton>
                      </DSBox>
                    </DSBox>
                  </DSGrid>

                  <DSGrid item xs={12}>
                    <DSBox
                      sx={{
                        display: "flex",
                        justifyContent: "right",
                        marginTop: "5px",
                      }}
                    >
                      <DSPrimaryButton
                        disabled={
                          !name || !giftCategoryImageUrl || !draftKlydoIds
                        }
                        onClick={() => {
                          setShowSaveModal(true);
                        }}
                      >
                        Save
                      </DSPrimaryButton>

                      {showSaveModal && (
                        <DSDialog
                          onClose={() => setShowSaveModal(false)}
                          open={showSaveModal}
                          maxWidth="sm"
                          fullWidth
                        >
                          <DSDialogContent>
                            <DSBox sx={{ margin: 3 }}>
                              <DSTypography
                                variant="h6"
                                sx={{ textAlign: "center", mb: 3 }}
                              >
                                You are about to save changes for {name}
                              </DSTypography>
                            </DSBox>
                            <DSGrid
                              container
                              spacing={2}
                              justifyContent="space-between"
                            >
                              <DSGrid item xs={5}>
                                <DSButton
                                  fullWidth
                                  variant="outlined"
                                  onClick={() => setShowSaveModal(false)}
                                >
                                  Cancel
                                </DSButton>
                              </DSGrid>
                              <DSGrid item xs={5}>
                                <ButtonLoader
                                  variant="outlined"
                                  className="w-100 h-100"
                                  title="Save"
                                  onClick={() => saveGiftCategory()}
                                />
                              </DSGrid>
                            </DSGrid>
                          </DSDialogContent>
                        </DSDialog>
                      )}
                    </DSBox>
                  </DSGrid>
                </DSGrid>
              </form>
            </DSGrid>
          </DSGrid>
        </DSBox>
      </DSBox>

      {isKlydoModalOpen && (
        <KlydosPickDialog
          selectedKlydos={draftKlydoIds}
          onPickKlydos={handleKlydoSelection}
          onClose={() => setIsKlydoModalOpen(false)}
          greet={true}
        />
      )}

      {isLogosModalOpen && (
        <LogosPickDialog
          selectedLogo={pendulumLogoUrl}
          onPickLogo={(logoUrl: string | undefined) => {
            setPendulumLogoUrl(logoUrl || "");
            setIsLogosModalOpen(false);
          }}
          onClose={() => setIsLogosModalOpen(false)}
          logos={props.logos || []}
          logosLoading={props.logosLoading || false}
        />
      )}
    </>
  );
};

export default GiftCategoryModal;
